import { Component, computed, effect, input } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, startWith, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  imports: [],
  selector: 'mat-error',
  styleUrls: ['./error-messages.component.scss'],
  template: `
    @if (control() && errorMessage()) {
      <ng-container>
        {{ errorMessage() }}
      </ng-container>
    }
  `,
})
export class ErrorMessagesComponent {
  public readonly control = input.required<AbstractControl | null>();

  private readonly errorsAdapter$ = new BehaviorSubject<Observable<Record<string, string> | null>>(
    new Observable<null>().pipe(startWith(null)),
  );

  private readonly formControlErrors = toSignal(
    this.errorsAdapter$.pipe(
      switchMap((valueObs) => valueObs),
      takeUntilDestroyed(),
    ),
    { initialValue: null },
  );

  public readonly errorMessage = computed(() => {
    const formControlErrors = this.formControlErrors();

    if (!formControlErrors) {
      return null;
    }

    for (const [errorName, error] of Object.entries(formControlErrors)) {
      if (error) {
        return this.getErrorMessage(errorName, error);
      }
    }

    return null;
  });

  private readonly staticErrors = {
    allFieldsRequired: this.translateService.instant('formValidationError.allFieldsRequired'),
    email: this.translateService.instant('formValidationError.email'),
    inactiveOption: this.translateService.instant('formValidationError.inactiveOption'),
    isNotUrl: this.translateService.instant('formValidationError.isNotUrl'),
    itemCategoryDuplicated: this.translateService.instant('formValidationError.itemCategoryDuplicated'),
    required: this.translateService.instant('formValidationError.required'),
    unknown: this.translateService.instant('formValidationError.unknown'),
    widgetRequestFailed: this.translateService.instant('formValidationError.widgetRequestFailed'),
  } as const;

  private readonly errors = {
    allFieldsRequired: () => this.staticErrors.allFieldsRequired,
    email: () => this.staticErrors.email,
    inactiveOption: () => this.staticErrors.inactiveOption,
    isNotUrl: () => this.staticErrors.isNotUrl,
    itemCategoryDuplicated: () => this.staticErrors.itemCategoryDuplicated,
    max: ({ max }: { max: number }) => this.translateService.instant('formValidationError.maxValue', { max }),
    maxlength: ({ requiredLength }: { requiredLength: number }) =>
      this.translateService.instant('formValidationError.maxLength', { requiredLength }),
    min: ({ min }: { min: number }) => this.translateService.instant('formValidationError.minValue', { min }),
    minlength: ({ requiredLength }: { requiredLength: number }) =>
      this.translateService.instant('formValidationError.minLength', { requiredLength }),
    pattern: () => this.translateService.instant('formValidationError.pattern'),
    required: () => this.staticErrors.required,
    unknown: () => this.staticErrors.unknown,
    widgetRequestFailed: () => this.staticErrors.widgetRequestFailed,
  } as const;

  private readonly showErrorsOnInput: readonly (keyof ErrorMessagesComponent['errors'])[] = ['maxlength', 'max'];

  constructor(private readonly translateService: TranslateService) {
    effect(() => {
      const control = this.control();

      if (!control) {
        return;
      }

      this.errorsAdapter$.next(
        control.statusChanges.pipe(
          map(() => control.errors),
          startWith(control.errors),
        ),
      );
    });

    effect(() => {
      const formControlErrors = this.formControlErrors();

      if (!formControlErrors) {
        return;
      }

      const errors = Object.entries(formControlErrors)
        .filter(([_, errorValue]) => Boolean(errorValue))
        .map(([errorKey]) => errorKey);

      if (errors.some((error) => (this.showErrorsOnInput as string[]).includes(error))) {
        this.control()?.markAsTouched();
      }
    });
  }

  private getErrorMessage(errorName: string, errorValue: any): string | null {
    return errorName in this.errors
      ? this.errors[errorName as keyof ErrorMessagesComponent['errors']](errorValue)
      : this.staticErrors.unknown;
  }
}
